/**
 * HtmlInclude - Load HTML sections from external files
 *
 * Usage:
 * <html-include src="sections/menu-bar.html"></html-include>
 *
 * Development: Fetches from src attribute
 * Production: Content is pre-injected by build script
 */

// Shared cache for loaded sections
const sectionCache = new Map();
const sectionLoadPromises = new Map();

class HtmlInclude extends HTMLElement {
    async connectedCallback() {
        const src = this.getAttribute('src');
        if (!src) {
            console.error('HtmlInclude: src attribute is required');
            return;
        }

        // Check if content is already inline (production mode)
        if (this.innerHTML.trim().length > 0) {
            console.debug(`Section already inline: ${src}`);
            return;
        }

        // Load from file (development mode)
        const html = await HtmlInclude.loadSection(src);
        if (html) {
            this.innerHTML = html;
        }
    }

    /**
     * Load HTML section from file (development mode)
     * In production, sections are pre-injected by build script
     */
    static async loadSection(src) {
        // Check cache first
        if (sectionCache.has(src)) {
            return sectionCache.get(src);
        }

        // Check if already loading
        if (sectionLoadPromises.has(src)) {
            await sectionLoadPromises.get(src);
            return sectionCache.get(src);
        }

        // Start loading
        const loadPromise = (async () => {
            try {
                const response = await fetch(`src/ui/${src}`);
                if (response.ok) {
                    const html = await response.text();
                    sectionCache.set(src, html);
                    return html;
                }
            } catch (error) {
                console.error(`Failed to load section: ${src}`, error);
            }

            sectionCache.set(src, null);
            return null;
        })();

        sectionLoadPromises.set(src, loadPromise);
        const result = await loadPromise;
        sectionLoadPromises.delete(src);

        return result;
    }
}

customElements.define('html-include', HtmlInclude);
